import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Collapse,
  Chip,
  useTheme
} from '@mui/material';
import { Hotel as HotelIcon } from '@mui/icons-material';
import SearchRooms from '../SearchRooms';
import RoomList from '../RoomList';
import BookingDrawer from '../BookingDrawer';
import dayjs from 'dayjs';
import { connect } from 'react-redux';

interface RoomDetail {
  name: string;
  description: string;
  pricePerNight: number;
  maxGuests: number;
  totalRooms: number;
  amenities: string[];
  facilities: string[];
  imageUrl: string;
}

interface WalkInBookingProps {
  roomDetails: RoomDetail[];
}

const WalkInBooking: React.FC<WalkInBookingProps> = ({ roomDetails }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const theme = useTheme();
  const [selectedRooms, setSelectedRooms] = useState<{
    categoryId: string;
    quantity: number;
  }[]>([]);
  const [searchParams, setSearchParams] = useState<{
    checkIn: Date | null;
    checkOut: Date | null;
    guests: number;
    isSearched: boolean;
  }>({
    checkIn: dayjs().hour(12).minute(0).second(0).toDate(), // Default check-in at 12 PM
    checkOut: dayjs().add(1, 'day').hour(11).minute(0).second(0).toDate(), // Default check-out at 11 AM
    guests: 1,
    isSearched: false,
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [_startDate, _setStartDate] = useState<dayjs.Dayjs | null>(dayjs());
  const [_endDate, _setEndDate] = useState<dayjs.Dayjs | null>(dayjs().add(7, 'day'));

  // Trigger initial search when component mounts
  useEffect(() => {
    handleSearch({
      checkIn: dayjs().hour(12).minute(0).second(0).toDate(),
      checkOut: dayjs().add(1, 'day').hour(11).minute(0).second(0).toDate(),
      guests: 1,
    });
  }, []);

  const handleSearch = (params: {
    checkIn: Date | null;
    checkOut: Date | null;
    guests: number;
  }) => {
    // Ensure check-in is at 12 PM and check-out is at 11 AM
    const checkInDate = dayjs(params.checkIn).hour(12).minute(0).second(0).toDate();
    const checkOutDate = dayjs(params.checkOut).hour(11).minute(0).second(0).toDate();
    
    setSearchParams({ 
      ...params, 
      checkIn: checkInDate,
      checkOut: checkOutDate,
      isSearched: true 
    });
    setSelectedRooms([]);
  };

  const handleDrawerClose = (isSuccess:any) => {
    setIsDrawerOpen(false);
    if(isSuccess){      
      // Reset the form after successful booking
      setSearchParams({
          checkIn: dayjs().hour(12).minute(0).second(0).toDate(),
          checkOut: dayjs().add(1, 'day').hour(11).minute(0).second(0).toDate(),
          guests: 1,
          isSearched: false,
        });
        setSelectedRooms([]);
        setShowSuccess(true);
        // Hide success message after 5 seconds
        setTimeout(() => setShowSuccess(false), 5000);
    }
  };

  const calculateTotalAmount = () => {
    if (!searchParams.checkIn || !searchParams.checkOut) return 0;

    const checkIn = dayjs(searchParams.checkIn);
    const checkOut = dayjs(searchParams.checkOut);
    
    // Calculate number of nights (inclusive of both check-in and check-out dates)
    const nights = checkOut.diff(checkIn, 'day') + 1;
    
    // Calculate total amount based on nights and room prices
    return selectedRooms.reduce((total, room) => {
      const roomDetail = roomDetails.find((rd: RoomDetail) => rd.name === room.categoryId);
      return total + (roomDetail?.pricePerNight || 0) * room.quantity * nights;
    }, 0);
  };

  const getBookingSummary = () => {
    if (!searchParams.checkIn || !searchParams.checkOut) return null;

    const checkIn = dayjs(searchParams.checkIn);
    const checkOut = dayjs(searchParams.checkOut);
    // Calculate number of nights (inclusive of both check-in and check-out dates)
    const nights = checkOut.diff(checkIn, 'day') + 1;

    return {
      nights,
      totalRooms: selectedRooms.reduce((total, room) => total + room.quantity, 0),
      totalAmount: calculateTotalAmount(),
      checkInTime: checkIn.format('hh:mm A'),
      checkOutTime: checkOut.format('hh:mm A'),
      checkInDate: checkIn.format('MMM DD, YYYY'),
      checkOutDate: checkOut.format('MMM DD, YYYY'),
    };
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        color: theme.palette.primary.main,
        fontWeight: 600,
        mb: 3
      }}>
        <HotelIcon /> Walk-in Guest Booking
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Create new bookings for walk-in guests who arrive at the hotel without prior reservations.
      </Typography>

      <Collapse in={showSuccess}>
        <Alert 
          severity="success" 
          sx={{ mb: 2 }}
          onClose={() => setShowSuccess(false)}
        >
          Booking created successfully!
        </Alert>
      </Collapse>

      <SearchRooms onSearch={handleSearch} />
      <>
        {getBookingSummary() && (
          <Box sx={{display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-start', gap: 1, p: 1, position: 'sticky', top: 0, bgcolor: '#fff', zIndex: 9, boxShadow: '0px 2px 12px -4px rgba(0,0,0,0.5)'}}>
            <Chip 
              label={`Selected Rooms: ${getBookingSummary()?.totalRooms}`}
              color="primary"
              variant="outlined"
            />
            <Chip 
              label={`Check-in: ${getBookingSummary()?.checkInDate} ${getBookingSummary()?.checkInTime}`}
              color="warning"
              variant="outlined"
            />
            <Chip 
              label={`Check-out: ${getBookingSummary()?.checkOutDate} ${getBookingSummary()?.checkOutTime}`}
              color="warning"
              variant="outlined"
            />
            <Chip 
              label={`Nights: ${getBookingSummary()?.nights}`}
              color="info"
              variant="outlined"
            />
            <Chip 
              label={`Total Amount: ₹${getBookingSummary()?.totalAmount.toLocaleString()}`}
              color="success"
              variant="outlined"
            />
          </Box>
        )}
      </>
      {searchParams.isSearched && (
        <Box sx={{mb: 8}}>
          <RoomList
            selectedRooms={selectedRooms}
            onRoomSelection={setSelectedRooms}
          />
          
          {selectedRooms.length > 0 && (
            <Box sx={{ 
              // mt: 3, 
              position: 'fixed', 
              bottom: '50px',
              right: 0,
              textAlign: 'right', 
              // bgcolor: '#fff', 
              padding: '16px',
              // boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
              zIndex: 1000
            }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', position: 'sticky', top: 0 }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  style={{marginLeft: 'auto'}}
                  onClick={() => setIsDrawerOpen(true)}
                >
                  Create Booking
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      )}

      <BookingDrawer
        open={isDrawerOpen}
        onClose={(isSuccess: any) => handleDrawerClose(isSuccess)}
        selectedRooms={selectedRooms}
        searchParams={searchParams}
      />
    </Box>
  );
};

const mapStateToProps = (state: any) => {
  return {
    roomDetails: state.common?.selectedRestaurant?.roomDetails || []
  };
};

export default connect(mapStateToProps)(WalkInBooking);